/**
 * Parses teacher question Excel uploads.
 *
 * Expected headers (row 1), case-insensitive; spaces allowed:
 *   Type | Marks | Question | Option A | Option B | Option C | Option D | Correct
 *
 * Type: mcq | MCQ | descriptive | Descriptive | long | essay
 * Correct (MCQ): A/B/C/D, 1-4 (1-based option), or 0-3 (0-based index).
 */

import * as XLSX from "xlsx";

function normCell(v) {
  if (v == null || v === "") return "";
  return String(v).trim();
}

function canonicalHeader(cell) {
  return normCell(cell)
    .toLowerCase()
    .replace(/\([^)]*\)/g, "") // strip "(correct)" etc from header labels
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Map noisy header keys to canonical field names */
function canonKey(raw) {
  const k = canonicalHeader(raw);
  const aliases = {
    type: "type",
    question_type: "type",
    kind: "type",
    marks: "marks",
    mark: "marks",
    question: "question",
    text: "question",
    stem: "question",
    option_a: "option_a",
    optiona: "option_a",
    option_b: "option_b",
    optionb: "option_b",
    option_c: "option_c",
    optionc: "option_c",
    option_d: "option_d",
    optiond: "option_d",
    correct: "correct",
    answer: "correct",
    key: "correct",
    ans: "correct",
  };
  return aliases[k] || k;
}

function parseType(raw) {
  const s = normCell(raw).toLowerCase();
  if (!s) return null;
  if (["mcq", "multiple", "multiple choice"].includes(s) || /^mcq|^multi/.test(s)) return "mcq";
  if (["descriptive", "desc", "long", "essay", "subjective"].includes(s) || /^desc/.test(s)) {
    return "descriptive";
  }
  return null;
}

/** correct cell → 0-based index 0–3 */
function parseCorrectIndex(raw) {
  const s = normCell(raw);
  if (!s && s !== 0) return 0;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const n = Math.floor(raw);
    if (n >= 0 && n <= 3) return n;
    if (n >= 1 && n <= 4) return n - 1;
  }
  const t = String(s).trim().toUpperCase();
  if (/^[ABCD]$/.test(t)) return t.charCodeAt(0) - 65;
  const n = parseInt(String(s), 10);
  if (Number.isFinite(n)) {
    if (n >= 1 && n <= 4) return n - 1;
    if (n >= 0 && n <= 3) return n;
  }
  return 0;
}

export function rowsToQuestionPayloads(subjectId, chapterId, rows) {
  const errors = [];
  const payloads = [];
  if (!rows.length) {
    errors.push({ row: 2, message: "Sheet is empty" });
    return { payloads, errors };
  }

  const headerRow = rows[0];
  const col = {};
  headerRow.forEach((h, i) => {
    const key = canonKey(h);
    if (["type", "marks", "question", "option_a", "option_b", "option_c", "option_d", "correct"].includes(key)) {
      col[key] = i;
    }
  });

  if (col.question == null) {
    errors.push({ row: 1, message: "Missing required column \"Question\"" });
    return { payloads, errors };
  }
  if (col.type == null) {
    errors.push({ row: 1, message: "Missing required column \"Type\"" });
    return { payloads, errors };
  }

  for (let r = 1; r < rows.length; r++) {
    const line = rows[r];
    const excelRow = r + 1;
    const get = (k) => (col[k] != null ? line[col[k]] : undefined);

    const typeRaw = get("type");
    const qType = parseType(typeRaw);
    const text = normCell(get("question"));
    const marksRaw = get("marks");
    const marks = marksRaw === "" || marksRaw == null ? 1 : Math.max(1, Math.floor(Number(marksRaw)) || 1);

    if (!text && !normCell(typeRaw)) continue;

    if (!text) {
      errors.push({ row: excelRow, message: "Question text is empty" });
      continue;
    }
    if (!qType) {
      errors.push({ row: excelRow, message: `Invalid Type "${normCell(typeRaw)}" (use MCQ or Descriptive)` });
      continue;
    }

    if (qType === "descriptive") {
      payloads.push({
        subject_id: subjectId,
        chapter_id: chapterId,
        type: "descriptive",
        text,
        marks,
        difficulty: "medium",
        options: null,
        correct_index: null,
      });
      continue;
    }

    const oa = normCell(get("option_a"));
    const ob = normCell(get("option_b"));
    const oc = normCell(get("option_c"));
    const od = normCell(get("option_d"));
    const options = [oa, ob, oc, od];
    const filled = options.map((x) => (x ? 1 : 0)).reduce((a, b) => a + b, 0);
    if (filled < 2) {
      errors.push({ row: excelRow, message: "MCQ needs at least two options (Option A–D)" });
      continue;
    }

    let correct = parseCorrectIndex(get("correct"));
    if (correct < 0 || correct > 3) correct = 0;
    if (!options[correct]) {
      const first = options.findIndex(Boolean);
      correct = first >= 0 ? first : 0;
    }

    payloads.push({
      subject_id: subjectId,
      chapter_id: chapterId,
      type: "mcq",
      text,
      marks,
      difficulty: "medium",
      options,
      correct_index: correct,
    });
  }

  return { payloads, errors };
}

export function parseQuestionExcelFile(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const sheetName = wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
  return rows;
}

export function downloadQuestionExcelTemplate() {
  const headers = [["Type", "Marks", "Question", "Option A", "Option B", "Option C", "Option D", "Correct"]];
  const sampleMcq = [
    "mcq",
    1,
    "What is 2 + 2?",
    "3",
    "4",
    "5",
    "6",
    "B",
  ];
  const sampleDesc = ["descriptive", 5, "Explain photosynthesis briefly.", "", "", "", "", ""];
  const ws = XLSX.utils.aoa_to_sheet([...headers, sampleMcq, sampleDesc]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Questions");
  XLSX.writeFile(wb, "questions_import_template.xlsx");
}
